import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult } from '@synergy/api';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { CustomerDocument } from '../models/customer-detail-document';

export const CUSTOMER_DETAIL_DOCUMENTS_FETCH = 'CUSTOMER_DETAIL_DOCUMENTS_FETCH';
export const CUSTOMER_DETAIL_DOCUMENTS_RECEIVED = 'CUSTOMER_DETAIL_DOCUMENTS_RECEIVED';
export const CUSTOMER_DETAIL_DOCUMENTS_RENAME = 'CUSTOMER_DETAIL_DOCUMENTS_RENAME';
export const CUSTOMER_DETAIL_DOCUMENTS_SET_VISIBILITY = 'CUSTOMER_DETAIL_DOCUMENTS_SET_VISIBILITY';
export const CUSTOMER_DETAIL_DOCUMENTS_DELETE = 'CUSTOMER_DETAIL_DOCUMENTS_DELETE';

export const CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_START = 'CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_START';
export const CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_PROGRESS = 'CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_PROGRESS';
export const CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_SUCCESS = 'CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_SUCCESS';
export const CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_FAILURE = 'CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_FAIL';
export const CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_CANCEL = 'CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_CANCEL';

const receiveDocuments = (customerDocuments: CustomerDocument[]) => ({
    type: CUSTOMER_DETAIL_DOCUMENTS_RECEIVED,
    customerDocuments,
});

const uploadDocumentStart = (document: CustomerDocument) => {
    return {
        type: CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_START,
        document,
    };
};

const uploadDocumentProgress = (id: number, progress: number) => {
    return {
        type: CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_PROGRESS,
        id,
        progress,
    };
};

const uploadDocumentCancel = (document: CustomerDocument) => {
    return {
        type: CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_CANCEL,
        document,
    };
};

const uploadDocumentError = (document: CustomerDocument) => {
    return {
        type: CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_FAILURE,
        document,
    };
};

const uploadDocumentSuccess = (id: number, document: CustomerDocument) => {
    return {
        type: CUSTOMER_DETAIL_DOCUMENTS_UPLOAD_SUCCESS,
        id,
        document,
    };
};

export const fetchCustomerDocuments = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CUSTOMER_DETAIL_DOCUMENTS_FETCH });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Documents.getDocuments(id);
        if (response.success) {
            dispatch(receiveDocuments(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

interface UploadInfo {
    file: File;
    customerDocument: CustomerDocument;
    response?: ApiResult<CustomerDocument>;
}

export const addCustomerDocuments = (files: File[], titles: string[] = []) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        const idBase = new Date().getTime() * -1;
        const uploadInfos = files.map<UploadInfo>((file, index) => ({
            file,
            customerDocument: {
                id: idBase - index,
                size: file.size,
                title: file.name,
                filesystem: '',
                uploadProgress: 0,
                isVisibleInShop: false,
            },
        }));
        uploadInfos.forEach(info => dispatch(uploadDocumentStart(info.customerDocument)));

        const uploadingDocumentPromises = uploadInfos.map(info =>
            api.contactsAndOrganizations.Customer.CustomerDetail.Documents.addDocument(
                customerId,
                info.file,
                c => (info.customerDocument.cancel = c),
                progress => {
                    const percentCompleted = Math.floor((progress.loaded * 100) / progress.total);
                    dispatch(uploadDocumentProgress(info.customerDocument.id, percentCompleted));
                }
            )
        );

        const responses = await Promise.all(uploadingDocumentPromises);
        responses.forEach((response, index) => (uploadInfos[index].response = response));

        // The same notice the documents give: stored the moment it is dropped (R-197).
        const saved = uploadInfos.filter(info => info.response?.success).length;
        if (saved > 0) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.upload.saved', { count: saved })));
        }

        uploadInfos.forEach((uploadInfo, index) => {
            if (uploadInfo.response?.success) {
                const document = uploadInfo.response.result;
                dispatch(uploadDocumentSuccess(uploadInfo.customerDocument.id, document));

                // The upload takes the file only; a title given with it follows.
                const title = titles[index]?.trim();
                if (title && title !== document.title) {
                    renameDocument(document.id, title)(dispatch, getState, { api });
                }
            } else {
                if (uploadInfo.response?.error instanceof Error) {
                    dispatch(uploadDocumentError(uploadInfo.customerDocument));
                } else {
                    dispatch(uploadDocumentCancel(uploadInfo.customerDocument));
                }
            }
        });
    };
};

export const renameDocument = (id: number, title: string) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        const customerDocument = getState().contactsAndOrganizations.customers.customerDetail.documents.documents.find(
            document => document.id === id
        );
        if (!customerId || !customerDocument) {
            return;
        }

        dispatch({ type: CUSTOMER_DETAIL_DOCUMENTS_RENAME, id, title });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Documents.updateDocument(
            customerId,
            {
                id: customerDocument.id,
                title,
            }
        );

        if (response.success) {
            dispatch(
                Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.documents.editTitleSuccess'))
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteDocument = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const customerId = getState().contactsAndOrganizations.customers.customerDetail.overview.customer?.id;
        if (!customerId) {
            return;
        }

        dispatch({ type: CUSTOMER_DETAIL_DOCUMENTS_DELETE, id });

        const response = await api.contactsAndOrganizations.Customer.CustomerDetail.Documents.deleteDocument(
            customerId,
            id
        );

        if (response.success) {
            dispatch(
                Snackbar.enqueue(PoolNotification.success('app.contactsAndOrganizations.documents.deleteSuccess'))
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
